import React, { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Button } from 'ksnm-common-ui/lib/Button';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Select } from 'ksnm-common-ui/lib/Select';
import { sortBy, uniqBy } from 'lodash-es';
import type { FC } from 'react';

import { indicatorsStore } from '@stores/dictionaryStores/indicatorsStore/indicatorsStore';
import type { IndicatorDtoDefId } from '@stores/dictionaryStores/indicatorsStore/indicatorsStore';

import styles from './Filter.module.scss';

interface IFilter {
  indicatorsWithGroup: IndicatorDtoDefId[];
}

export const Filter: FC<IFilter> = observer(({ indicatorsWithGroup }): JSX.Element => {
  const { setFilters, filters, setSelectNode, setSelectedKeys } = indicatorsStore;

  const { reportGroup, reportVersion } = filters;

  const onClear = useCallback(() => {
    setFilters({});
    setSelectNode(null);
    setSelectedKeys([]);
  }, [setFilters, setSelectNode, setSelectedKeys]);

  const onChangeReportGroup = useCallback(
    (v) => {
      setFilters({ ...filters, reportGroup: v });
      setSelectNode(null);
      setSelectedKeys([]);
    },
    [filters, setFilters, setSelectNode, setSelectedKeys],
  );

  const onChangeReportVersion = useCallback(
    (v) => {
      setFilters({ ...filters, reportVersion: v });
      setSelectNode(null);
      setSelectedKeys([]);
    },
    [filters, setFilters, setSelectNode, setSelectedKeys],
  );

  const reportGroupItems = useMemo(
    () =>
      sortBy(
        uniqBy(
          indicatorsWithGroup.filter((item) => {
            if (reportVersion && item.repVers !== reportVersion) {
              return false;
            }
            return !!item.reportGroupCode;
          }),
          'reportGroupCode',
        )
          .filter((item) => !!item.reportGroupCode)
          .map((indicator) => ({
            id: indicator.reportGroupCode,
            name: `${indicator.reportGroupCode} - ${indicator.reportGroupName}`,
          })),
        'name',
      ),
    [indicatorsWithGroup, reportVersion],
  );

  const reportVersionItems = useMemo(
    () =>
      sortBy(
        uniqBy(
          indicatorsWithGroup.filter((item) => {
            if (reportGroup && item.reportGroupCode !== reportGroup) {
              return false;
            }
            return !!item.repVers;
          }),
          'repVers',
        ).map((indicator) => ({
          id: indicator.repVers,
          name: `${indicator.repVers} - ${indicator.reportVersionName}`,
        })),
        'name',
      ),
    [indicatorsWithGroup, reportGroup],
  );

  return (
    <div className={styles.designFilters}>
      <div className={styles.items}>
        <Select
          items={reportGroupItems}
          placeholder="Группа показателя"
          value={filters.reportGroup}
          onChange={onChangeReportGroup}
          size="small"
          showSearch
          className={styles.select}
        />
        <Select
          items={reportVersionItems}
          placeholder="Версия показателя"
          value={filters.reportVersion}
          onChange={onChangeReportVersion}
          size="small"
          showSearch
          className={styles.select}
        />
      </div>

      <Button onClick={onClear} disabled={!(reportGroup || reportVersion)} icon={<Icon name="stop" />}>
        Сбросить фильтры
      </Button>
    </div>
  );
});
